<template>
  <LoadingContainer :is-loading="isLoadingItems">
    <div class="columns">
      <div class="column is-three-quarters">
        <form novalidate @submit.prevent="submit">
          <div class="card">
            <div class="card-header">
              <div class="card-header-title">
                <div class="title is-6 is-marginless">
                  Προσθήκη Μάρκας
                </div>
              </div>
              <div class="card-header-icon">
                <div class="field has-addons is-marginless">
                  <div class="control">
                    <router-link
                      :to="{ name: 'products.brands.list' }"
                      class="button"
                    >
                      <span class="icon is-small">
                        <i class="fa fa-list-ul" />
                      </span>
                      <span>Λίστα</span>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
            <div class="card-content">
              <AddGeneralDetails
                :general="general"
                @updateGeneral="updateGeneral"
              />
            </div>
          </div>

          <ToggleableCard :is-open="true" title="Brand Banner (WEBP)">
            <div class="card-content">
              <AddPhotos
                :title="'Εικόνα (WEBP)'"
                :preview="bannerPreview"
                :show-error="errors.has('banner_image')"
                rule="image/webp"
                @addPhotos="handleAddBanner"
              />
              <p class="help">Το αρχείο πρέπει να είναι WEBP.</p>

              <div v-if="bannerPreview" class="mt-2">
                <button
                  type="button"
                  class="button is-small"
                  @click="removeBanner"
                >
                  <span class="icon is-small">
                    <i class="fa fa-trash" />
                  </span>
                  <span>Αφαίρεση</span>
                </button>
              </div>
            </div>
          </ToggleableCard>

          <div class="card">
            <div class="card-footer">
              <FormSubmit :is-saving="isSaving" @reset="reset" />
            </div>
          </div>
        </form>
      </div>
      <div class="column">
        <ExistingBrands :items="items" />
      </div>
    </div>
  </LoadingContainer>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import request from '@/utils/request';
import AddItem from '@/views/components/AddItem';
import AddGeneralDetails from '@/views/components/AddGeneralDetails';
import ToggleableCard from '@/views/components/ToggleableCard';
import LoadingContainer from '@/views/components/LoadingContainer';
import AddPhotos from '@/views/components/AddPhotos';
import FormSubmit from '@/views/components/FormSubmit';
import ExistingBrands from '../../components/ExistingBrands';
import ExistingCollections from '../../components/ExistingCollections';

export default {
  components: {
    AddGeneralDetails,
    ToggleableCard,
    LoadingContainer,
    AddPhotos,
    FormSubmit,
    ExistingBrands,
    ExistingCollections,
  },
  extends: AddItem,
  data() {
    return {
      general: {
        title: '',
        slug: '',
        description: '',
        content: '',
        active: false,
      },
      bannerImage: null,
      bannerPreview: '',
      bannerRemoved: false,
      createdUuid: null,
      bannerReadToken: 0,
    };
  },
  computed: {
    ...mapGetters({
      items: 'brands/list/getBrands',
      collections: 'collections/all/getCollections',
      isLoadingItems: 'brands/list/getIsLoading',
    }),
  },
  async created() {
    try {
      await this.getBrands();
    } catch (err) {
      this.$router.push('/error').catch(e => console.log(e));
    }
  },
  methods: {
    ...mapActions({
      getBrands: 'brands/list/getBrands',
    }),

    handleAddBanner(image) {
      const file = Array.isArray(image) ? image[0] : image;

      this.bannerImage = file;
      this.bannerRemoved = false;

      this.bannerReadToken += 1;
      const currentToken = this.bannerReadToken;

      const reader = new FileReader();
      this.$validator
        .validateAll()
        .then(() => {
          reader.onload = e => {
            if (currentToken !== this.bannerReadToken) return;
            if (this.bannerImage !== file) return;
            if (this.bannerRemoved) return;

            this.bannerPreview = e && e.target ? e.target.result : '';
            this.errors.remove('banner_image');
          };
          reader.readAsDataURL(file);
        })
        .catch(() => {});
    },

    removeBanner() {
      this.bannerReadToken += 1;
      this.bannerImage = null;
      this.bannerPreview = '';
      this.bannerRemoved = true;
      this.errors.remove('banner_image');
    },

    async submit() {
      try {
        const validations = await Promise.all(
          this.$children.map(child => child.$validator.validateAll()),
        );
        const isValid = validations.every(Boolean);

        if (!isValid) {
          throw Error('Client-side validation failed');
        }

        this.isSaving = true;

        let { createdUuid } = this;

        if (!createdUuid) {
          const createResp = await request.post('/brands', {
            ...this.general,
          });

          const createdBrand =
            (createResp && createResp.data && createResp.data.brand) || null;

          createdUuid =
            createdBrand && createdBrand.uuid ? createdBrand.uuid : null;

          if (!createdUuid) {
            throw Error('Missing brand uuid from create response');
          }

          this.createdUuid = createdUuid;
        }

        const hasBannerFile =
          this.bannerImage &&
          typeof File !== 'undefined' &&
          this.bannerImage instanceof File;

        const shouldCallBanner = hasBannerFile || this.bannerRemoved === true;

        if (shouldCallBanner) {
          const fd = new FormData();
          fd.append('banner_image_remove', this.bannerRemoved ? '1' : '0');

          if (hasBannerFile) {
            fd.append('banner_image', this.bannerImage);
          }

          const bannerResp = await request.post(
            `/brands/${String(createdUuid)}/banner`,
            fd,
            { headers: { 'Content-Type': 'multipart/form-data' } },
          );

          if (!bannerResp || bannerResp.status !== 200) {
            const m =
              (bannerResp && bannerResp.data && bannerResp.data.message) ||
              'To αίτημα απέτυχε';
            throw new Error(m);
          }
        }

        this.isSaving = false;
        this.createdUuid = null;

        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Η μάρκα αποθηκεύτηκε',
        });

        await this.getBrands();
        this.$router.push({ name: 'products.brands.list' }).catch(() => {});
      } catch (err) {
        this.isSaving = false;

        const raw =
          (err &&
            err.response &&
            err.response.data &&
            err.response.data.message) ||
          (err && err.message) ||
          'To αίτημα απέτυχε';

        const msg =
          this.createdUuid && raw
            ? `${raw} (Η μάρκα έχει ήδη δημιουργηθεί. Πατήστε ξανά Αποθήκευση για να ξαναδοκιμάσετε το banner.)`
            : raw;

        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: msg,
        });
      }
    },

    reset() {
      this.general = {
        title: '',
        slug: '',
        description: '',
        content: '',
        active: false,
      };

      this.bannerReadToken += 1;
      this.bannerImage = null;
      this.bannerPreview = '';
      this.bannerRemoved = false;
      this.createdUuid = null;
      this.errors.remove('banner_image');

      this.$children.forEach(child => child.$validator.reset());
    },

    updateGeneral(general) {
      this.general = general;
    },
  },
};
</script>
